"use client";

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
  type ReactNode,
  type RefObject,
} from "react";
import {
  getCalendarMonth,
  getCalendarUpcoming,
  getCalendarToday,
  getCalendarEkadashi,
  getCalendarVratYear,
} from "@/lib/api/calendar";
import type { CalendarFestival, CalendarTodayResponse } from "@/lib/api/calendar";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import {
  CalendarTodayCard,
  CalendarGrid,
  FestivalCard,
  EkadashiSection,
  MONTH_NAMES,
  formatDateLong,
  typeLabel,
} from "@/components/calendar";
import { IST_TIME_ZONE } from "@/lib/ist-date";
import { BELOW_STICKY_CHROME_SCROLL_MARGIN } from "@/lib/sticky-chrome-offsets";
import { civilDateInZone } from "@/lib/timing-city-param";

import {
  CALENDAR_DATE_MAX,
  CALENDAR_DATE_MIN,
  NO_VRAT_ROWS,
  emptyDaySentence,
  engineRows,
  fastingNote,
  fastsKeptOn,
  isCalendarDate,
  keptFastNote,
  mergeMonthRows,
  monthKey,
  observancesOn,
  panchangHref,
  todayCardExtras,
  vratYearOrNull,
  yearMonthOf,
  type CalendarDateRow,
  type CalendarInitial,
  type VratYearRows,
} from "./_date/calendar-date-answer";

const NO_LINKABLE_FESTIVALS: readonly string[] = [];

/** A month's rows in the browser: read, failed, or (absent) not read yet. */
type MonthEntry =
  | {
      status: "ready";
      rows: CalendarDateRow[];
      /** False when the year's vrat dates could not be read: festival rows only. */
      complete: boolean;
    }
  | { status: "failed" };

/**
 * With no server props (a test, or a render without the server reads), start
 * from India's today and read everything in the browser, as before.
 */
function browserInitial(): CalendarInitial {
  const today = civilDateInZone(new Date(), IST_TIME_ZONE);
  return {
    ...yearMonthOf(today),
    today,
    todayData: null,
    monthRows: null,
    monthComplete: false,
  };
}

/**
 * The server render lacked a read: the public HTML cache must not keep this
 * copy for the day (opennext-public-html-cache.js refuses a page carrying
 * `data-ishvaram-degraded`), so the next request tries the server reads again.
 */
function degradedMarker(initial: CalendarInitial | null): string | undefined {
  if (!initial) return undefined;
  return initial.todayData && initial.monthRows && initial.monthComplete
    ? undefined
    : "calendar-initial";
}

async function readVratYear(year: number): Promise<VratYearRows | null> {
  return vratYearOrNull(await getCalendarVratYear(year), year);
}

/**
 * Today's observances (from the server, or read here when it could not) and
 * the next 30 days. Current observance is primary value; upcoming is an
 * independent depth feed.
 */
function useTodayFeeds(start: CalendarInitial, markCalendarReady: () => void) {
  const [todayData, setTodayData] = useState<CalendarTodayResponse | null>(start.todayData);
  const [todayFailed, setTodayFailed] = useState(false);
  const [upcoming, setUpcoming] = useState<CalendarFestival[]>([]);
  // The server's reads already make the first view usable.
  const serverToday = start.todayData !== null;
  const serverReady = serverToday || start.monthRows !== null;
  useEffect(() => {
    if (serverReady) markCalendarReady();
    if (!serverToday) {
      void getCalendarToday()
        .then((today) => {
          if (!today) {
            setTodayFailed(true);
            return;
          }
          setTodayData(today);
          markCalendarReady();
        })
        .catch(() => setTodayFailed(true));
    }
    void getCalendarUpcoming(30)
      .then((up) => setUpcoming(up.festivals))
      .catch(() => {});
  }, [markCalendarReady, serverReady, serverToday]);
  return { todayData, todayFailed, upcoming };
}

/**
 * Each shown month's merged rows, keyed by YYYY-MM. The server's month is
 * held from the start; any other is its festival list plus the year's vrat
 * dates (one read per year), merged. A failed month is asked for again the
 * next time it is shown; a failed vrat year on the next month that needs it.
 */
function useMonthRows(
  start: CalendarInitial,
  year: number,
  month: number,
  markCalendarReady: () => void
): Record<string, MonthEntry> {
  const startKey = monthKey(start.year, start.month);
  const [months, setMonths] = useState<Record<string, MonthEntry>>(() =>
    start.monthRows
      ? {
          [startKey]: { status: "ready", rows: start.monthRows, complete: start.monthComplete },
        }
      : {}
  );
  // Months already asked for (or held from the server), and each year's vrat
  // read, so a month or a year is read once however often it is shown.
  const requestedMonths = useRef(new Set<string>(start.monthRows ? [startKey] : []));
  const vratYears = useRef(new Map<number, Promise<VratYearRows | null>>());
  const readVrat = useCallback((vratYear: number) => {
    let vrat = vratYears.current.get(vratYear);
    if (!vrat) {
      vrat = readVratYear(vratYear).catch(() => null);
      vratYears.current.set(vratYear, vrat);
    }
    return vrat;
  }, []);

  // The server held the month's festival list but not the year's vrat dates:
  // read only those here and merge them in (the month is not read again).
  useEffect(() => {
    const festivalRows = start.monthRows;
    if (!festivalRows || start.monthComplete) return;
    void readVrat(start.year).then((vratRows) => {
      if (!vratRows) {
        vratYears.current.delete(start.year);
        return;
      }
      const rows = mergeMonthRows(festivalRows, vratRows, start.year, start.month);
      startTransition(() =>
        setMonths((prev) => ({ ...prev, [startKey]: { status: "ready", rows, complete: true } }))
      );
    });
  }, [start, startKey, readVrat]);

  useEffect(() => {
    const key = monthKey(year, month);
    if (requestedMonths.current.has(key)) return;
    requestedMonths.current.add(key);
    Promise.all([getCalendarMonth(year, month), readVrat(year)])
      .then(([data, vratRows]) => {
        if (!vratRows) vratYears.current.delete(year);
        const rows = mergeMonthRows(
          engineRows(data?.festivals),
          vratRows ?? NO_VRAT_ROWS,
          year,
          month
        );
        startTransition(() =>
          setMonths((prev) => ({
            ...prev,
            [key]: { status: "ready", rows, complete: vratRows !== null },
          }))
        );
        markCalendarReady();
      })
      .catch(() => {
        requestedMonths.current.delete(key);
        startTransition(() => setMonths((prev) => ({ ...prev, [key]: { status: "failed" } })));
      });
  }, [year, month, markCalendarReady, readVrat]);
  return months;
}

function useEkadashis(ekadashiYear: number): CalendarFestival[] {
  const [ekadashis, setEkadashis] = useState<CalendarFestival[]>([]);
  useEffect(() => {
    getCalendarEkadashi(ekadashiYear)
      .then((data) => setEkadashis(data.ekadashis))
      .catch(() => {});
  }, [ekadashiYear]);
  return ekadashis;
}

export default function CalendarClient({
  crossLinks,
  resultOffer,
  linkableFestivalIds = NO_LINKABLE_FESTIVALS,
  initial = null,
}: {
  crossLinks: ReactNode;
  resultOffer: ReactNode;
  /**
   * Festivals that serve their own page, from the server (the catalog stays out
   * of this bundle). Any other id the calendar API lists renders unlinked: the
   * monthly Pradosh/Sankashti occurrences answer 410 since 2026-09-23.
   */
  linkableFestivalIds?: readonly string[];
  /**
   * India's today and the server's reads for it (load-calendar-initial.ts):
   * the first render shows them, so the HTML carries the month's observances
   * and the browser clock never picks the month or the highlighted day.
   */
  initial?: CalendarInitial | null;
}) {
  const [start] = useState(() => initial ?? browserInitial());
  const [year, setYear] = useState(start.year);
  const [month, setMonth] = useState(start.month);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [answerDate, setAnswerDate] = useState(start.today);
  const [dateInput, setDateInput] = useState(start.today);
  const [ekadashiYear, setEkadashiYear] = useState(start.year);
  const completionTrackedRef = useRef(false);
  const selectedDayRef = useRef<HTMLDivElement | null>(null);
  const linkable = useMemo(() => new Set(linkableFestivalIds), [linkableFestivalIds]);
  const festivalHref = useCallback(
    (id: string) => (linkable.has(id) ? `/calendar/festival/${id}/` : null),
    [linkable]
  );

  const markCalendarReady = useCallback(() => {
    if (completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackCalculatorUsed({ calculator: "calendar", source_path: "/calendar" });
  }, []);

  const { todayData, todayFailed, upcoming } = useTodayFeeds(start, markCalendarReady);
  const months = useMonthRows(start, year, month, markCalendarReady);
  const ekadashis = useEkadashis(ekadashiYear);

  const changeMonth = (delta: number) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    if (newYear >= 2026 && newYear <= 2027) {
      setMonth(newMonth);
      setYear(newYear);
      setSelectedDate(null);
    }
  };

  // A picked date answers in the box and brings its month into the grid.
  const pickDate = (value: string) => {
    setDateInput(value);
    if (!isCalendarDate(value)) return;
    const picked = yearMonthOf(value);
    setAnswerDate(value);
    setYear(picked.year);
    setMonth(picked.month);
    setSelectedDate(null);
  };

  const entry = months[monthKey(year, month)];
  const loading = entry?.status !== "ready";
  const monthRows = entry?.status === "ready" ? entry.rows : NO_ROWS;
  const answerMonth = yearMonthOf(answerDate);
  const answerEntry = months[monthKey(answerMonth.year, answerMonth.month)];
  // Today's month rows, so the Today card lists what the answer box does.
  const todayEntry = months[monthKey(start.year, start.month)];
  const todayRows = todayEntry?.status === "ready" ? todayEntry.rows : NO_ROWS;
  const todayExtras = useMemo(
    () => (todayData ? todayCardExtras(todayData, todayRows, start.today) : null),
    [todayData, todayRows, start.today]
  );

  // Events for selected date
  const selectedEvents = useMemo(
    () => (selectedDate ? observancesOn(monthRows, selectedDate) : NO_ROWS),
    [selectedDate, monthRows]
  );

  // The details open below the grid, which on a phone is off-screen: bring
  // them up when a day is chosen (they are why the seeker tapped it).
  const scrollToSelected = useRef(false);
  const selectDate = (date: string) => {
    scrollToSelected.current = true;
    setSelectedDate(date);
  };
  useEffect(() => {
    if (!scrollToSelected.current || !selectedDayRef.current) return;
    scrollToSelected.current = false;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    selectedDayRef.current.scrollIntoView({
      block: "start",
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [selectedDate]);

  return (
    <div
      className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12"
      data-ishvaram-degraded={degradedMarker(initial)}
    >
      {/* Today card: its slot is reserved from the first paint, so the late
          fetch fills it instead of pushing the month grid down (b-content-08:
          the grid jumped 314px, CLS 0.24-0.47). */}
      <div data-calendar-today-slot className="mb-6 min-h-44 sm:min-h-32">
        {todayData && todayExtras ? (
          <CalendarTodayCard
            data={todayData}
            extraEvents={todayExtras.observances}
            fastsKept={todayExtras.fastsKept}
            isFastingDay={todayExtras.fasting}
          />
        ) : todayFailed ? (
          <p className="text-body-small text-text-muted">
            Today&apos;s observances could not be loaded. The month calendar below still works.
          </p>
        ) : (
          <div
            aria-hidden="true"
            className="min-h-44 sm:min-h-32 rounded-xl border border-border-default bg-surface-raised motion-safe:animate-pulse"
          />
        )}
      </div>

      <DateAnswer
        date={answerDate}
        inputValue={dateInput}
        onPick={pickDate}
        entry={answerEntry}
        today={start.today}
        festivalHref={festivalHref}
      />

      <MonthNavigation year={year} month={month} onChangeMonth={changeMonth} />

      <MonthGrid
        year={year}
        month={month}
        loading={loading}
        festivals={monthRows}
        onSelect={selectDate}
        selectedDate={selectedDate}
        today={start.today}
      />

      <SelectedDateEvents
        selectedDate={selectedDate}
        selectedEvents={selectedEvents}
        festivalHref={festivalHref}
        containerRef={selectedDayRef}
      />

      {(todayData || !loading) && resultOffer}

      <MonthEvents year={year} month={month} festivals={monthRows} festivalHref={festivalHref} />

      <UpcomingEvents upcoming={upcoming} festivalHref={festivalHref} />

      <EkadashiTracker
        ekadashiYear={ekadashiYear}
        ekadashis={ekadashis}
        onYearChange={setEkadashiYear}
      />

      <div className="mt-12">{crossLinks}</div>
    </div>
  );
}

const NO_ROWS: CalendarDateRow[] = [];

/**
 * "What's on this date?": a date in the page's 2026-2027 range, answered with
 * every row the calendar holds for it. The answer box keeps one height through
 * its states (checking, answer, empty day, error), so a late month read fills
 * it without moving the month grid below.
 */
function DateAnswer({
  date,
  inputValue,
  onPick,
  entry,
  today,
  festivalHref,
}: {
  date: string;
  inputValue: string;
  onPick: (value: string) => void;
  entry: MonthEntry | undefined;
  today: string;
  festivalHref: (id: string) => string | null;
}) {
  return (
    <section
      aria-labelledby="calendar-date-answer-heading"
      data-calendar-date-answer
      className="mb-6 rounded-xl border border-border-default bg-surface-raised p-4"
    >
      <h2
        id="calendar-date-answer-heading"
        className="mb-1 font-serif text-body font-bold text-text-primary"
      >
        What&apos;s on this date?
      </h2>
      <label htmlFor="calendar-date-input" className="block text-body-small text-text-secondary">
        Pick a date in 2026 or 2027 to see its festivals and vrats.
      </label>
      <input
        id="calendar-date-input"
        type="date"
        min={CALENDAR_DATE_MIN}
        max={CALENDAR_DATE_MAX}
        value={inputValue}
        onChange={(event) => onPick(event.target.value)}
        className="input-sacred mt-2 rounded-lg px-3 py-1.5 text-body text-text-primary"
      />
      <div
        data-calendar-date-answer-box
        aria-live="polite"
        className="mt-3 min-h-[252px] sm:min-h-[228px]"
      >
        {/* Keyed by date and state: each answer mounts fresh instead of React
            moving the reused date text inside a reused paragraph, which the
            browser records as a layout shift once a late month read lands. */}
        <DateAnswerBody
          key={`${date}:${entry?.status ?? "checking"}`}
          date={date}
          entry={entry}
          today={today}
          festivalHref={festivalHref}
        />
      </div>
    </section>
  );
}

function DateAnswerBody({
  date,
  entry,
  today,
  festivalHref,
}: {
  date: string;
  entry: MonthEntry | undefined;
  today: string;
  festivalHref: (id: string) => string | null;
}) {
  const day = formatDateLong(date);
  if (!entry) {
    return <p className="text-body-small text-text-muted">Checking {day}…</p>;
  }
  if (entry.status === "failed") {
    return (
      <p className="text-body-small text-text-muted">
        The calendar for {day} could not be loaded. Try again in a moment.
      </p>
    );
  }
  const rows = observancesOn(entry.rows, date);
  const kept = fastsKeptOn(entry.rows, date);
  const incomplete = entry.complete ? null : (
    <p className="mt-2 text-caption text-text-muted">
      This year&apos;s monthly tithi dates (Purnima, Amavasya, Masik Shivratri, Sankranti) could not
      be loaded, so they are not listed.
    </p>
  );
  if (rows.length === 0 && kept.length === 0) {
    return (
      <>
        <p className="text-body-small text-text-primary">
          {entry.complete
            ? emptyDaySentence(date)
            : `No festival in this calendar's festival list falls on ${day}.`}
        </p>
        {incomplete}
        <Link
          href={panchangHref(date, today)}
          prefetch={false}
          className="mt-2 inline-block text-body-small font-semibold text-intent-brand underline underline-offset-2"
        >
          Open the Panchang for {day} →
        </Link>
      </>
    );
  }
  return (
    <>
      <p className="text-body-small font-semibold text-text-primary">On {day}:</p>
      <ul className="mt-2 space-y-2">
        {rows.map((row) => (
          <DateAnswerRow key={row.id} row={row} href={festivalHref(row.id)} />
        ))}
        {kept.map((row) => (
          <li key={`kept-${row.id}`}>
            <span className="text-body-small font-semibold text-text-primary">{row.name_en}</span>
            <span className="block text-caption text-text-muted">{keptFastNote(row)}</span>
          </li>
        ))}
      </ul>
      {incomplete}
    </>
  );
}

function DateAnswerRow({ row, href }: { row: CalendarDateRow; href: string | null }) {
  const note = fastingNote(row);
  const meta = [typeLabel(row.type), row.fasting ? "Fasting" : null, note]
    .filter((part): part is string => part !== null)
    .join(" · ");
  return (
    <li>
      {href ? (
        <Link
          href={href}
          prefetch={false}
          className="text-body-small font-semibold text-intent-brand underline underline-offset-2"
        >
          {row.name_en}
        </Link>
      ) : (
        <span className="text-body-small font-semibold text-text-primary">{row.name_en}</span>
      )}
      <span className="block text-caption text-text-muted">{meta}</span>
    </li>
  );
}

function MonthNavigation({
  year,
  month,
  onChangeMonth,
}: {
  year: number;
  month: number;
  onChangeMonth: (delta: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mb-6">
      <button
        type="button"
        onClick={() => onChangeMonth(-1)}
        disabled={year === 2026 && month === 1}
        className="px-4 py-2 rounded-lg border border-ish-border bg-white text-ish-dark text-sm font-semibold hover:bg-ish-accent/5 disabled:opacity-30 disabled:hover:bg-white transition-colors"
      >
        Prev
      </button>
      <h2 className="font-serif text-xl font-bold text-ish-dark m-0">
        {MONTH_NAMES[month - 1]} {year}
      </h2>
      <button
        type="button"
        onClick={() => onChangeMonth(1)}
        disabled={year === 2027 && month === 12}
        className="px-4 py-2 rounded-lg border border-ish-border bg-white text-ish-dark text-sm font-semibold hover:bg-ish-accent/5 disabled:opacity-30 disabled:hover:bg-white transition-colors"
      >
        Next
      </button>
    </div>
  );
}

function MonthGrid({
  year,
  month,
  loading,
  festivals,
  onSelect,
  selectedDate,
  today,
}: {
  year: number;
  month: number;
  loading: boolean;
  festivals: CalendarDateRow[];
  onSelect: (date: string) => void;
  selectedDate: string | null;
  today: string;
}) {
  // While the month loads, the same grid renders without observances: it
  // holds the final height (a ~200px spinner used to grow into the grid).
  return (
    <div
      className="bg-white rounded-2xl border border-ish-border p-4 shadow-sm mb-8"
      aria-busy={loading || undefined}
    >
      {loading ? <p className="sr-only">Loading calendar...</p> : null}
      <CalendarGrid
        year={year}
        month={month}
        festivals={loading ? NO_FESTIVALS : festivals}
        onSelect={onSelect}
        selectedDate={selectedDate}
        today={today}
      />
    </div>
  );
}

const NO_FESTIVALS: CalendarDateRow[] = [];

function SelectedDateEvents({
  selectedDate,
  selectedEvents,
  festivalHref,
  containerRef,
}: {
  selectedDate: string | null;
  selectedEvents: CalendarDateRow[];
  festivalHref: (id: string) => string | null;
  containerRef: RefObject<HTMLDivElement | null>;
}) {
  if (!selectedDate || selectedEvents.length === 0) return null;
  return (
    <div
      ref={containerRef}
      data-calendar-selected-day
      className={`mt-8 ${BELOW_STICKY_CHROME_SCROLL_MARGIN} animate-in fade-in slide-in-from-bottom-2`}
    >
      <h3 className="text-base font-bold text-ish-dark mb-4 px-1">
        {formatDateLong(selectedDate)}
      </h3>
      <div className="flex flex-col gap-3">
        {selectedEvents.map((festival) => (
          <FestivalCard
            key={festival.id + festival.date}
            festival={festival}
            href={festivalHref(festival.id)}
          />
        ))}
      </div>
    </div>
  );
}

function MonthEvents({
  year,
  month,
  festivals,
  festivalHref,
}: {
  year: number;
  month: number;
  festivals: CalendarDateRow[];
  festivalHref: (id: string) => string | null;
}) {
  if (festivals.length === 0) return null;
  return (
    <div className="mt-12">
      <h2 className="font-serif text-xl font-bold text-ish-dark mb-6">
        All Events in {MONTH_NAMES[month - 1]} {year}
      </h2>
      <div className="flex flex-col gap-3">
        {festivals.map((festival) => (
          <FestivalCard
            key={festival.id + festival.date}
            festival={festival}
            href={festivalHref(festival.id)}
          />
        ))}
      </div>
    </div>
  );
}

function UpcomingEvents({
  upcoming,
  festivalHref,
}: {
  upcoming: CalendarFestival[];
  festivalHref: (id: string) => string | null;
}) {
  if (upcoming.length === 0) return null;
  return (
    <div className="mt-12">
      <h2 className="font-serif text-xl font-bold text-ish-dark mb-6">
        Upcoming Festivals (Next 30 Days)
      </h2>
      <div className="flex flex-col gap-3">
        {upcoming.map((festival) => (
          <FestivalCard
            key={festival.id + festival.date}
            festival={festival}
            href={festivalHref(festival.id)}
          />
        ))}
      </div>
    </div>
  );
}

function EkadashiTracker({
  ekadashiYear,
  ekadashis,
  onYearChange,
}: {
  ekadashiYear: number;
  ekadashis: CalendarFestival[];
  onYearChange: (year: number) => void;
}) {
  return (
    <div className="mt-12 border-t border-ish-border pt-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="font-serif text-xl font-bold text-ish-dark m-0">Ekadashi Tracker</h2>
        <div className="flex p-1 bg-black/[0.03] rounded-lg self-start">
          {[2026, 2027].map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => onYearChange(year)}
              className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${
                ekadashiYear === year
                  ? "bg-white text-ish-dark shadow-sm"
                  : "text-ish-text-muted hover:text-ish-dark"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
      <EkadashiSection ekadashis={ekadashis} />
    </div>
  );
}
